/**
 * Compose (§7.3, left region).
 *
 * Four things this panel must get right, all of them about not surprising the
 * actor after the fact:
 *  1. The cost quote is fetched and shown BEFORE the job is submitted, naming
 *     whether it draws included credits, the actor's own credits, or bills as
 *     overage — and to whom.
 *  2. Spend caps are shown as remaining headroom, not discovered on failure.
 *  3. Generation and posting are separate steps with separate confirmations.
 *     Generated content is never auto-posted.
 *  4. A post-moderation block is explained here with its category, never as a
 *     generic failure.
 */

import { useMemo, useState } from 'react';
import { useApi, useMutation } from '../../hooks/useApi';
import type {
  Action,
  Asset,
  Bot,
  GenerationJob,
  GenerationKind,
  GenerationQuote,
  Page,
  SpendCap,
} from '../../api/types';
import type { CapabilityResolver } from '../../hooks/useCapabilities';
import { Gate } from '../../components/Gate';
import { Card, Field, LiveRegion, Badge } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { Dialog } from '../../components/Dialog';
import { amountLabel, humanize } from '../../lib/format';
import { routes } from '../../api/routes';
import { preferredAccount } from '../../lib/accounts';

function fundingSentence(q: GenerationQuote): string {
  switch (q.funding) {
    case 'included_credits':
      return `Draws the rental's included credits (${q.included_credits_remaining} left after this).`;
    case 'actor_credits':
      return `Draws your own generation credits (${q.actor_credits_remaining} left after this).`;
    case 'overage':
      return `Bills as overage to @${q.billed_to_handle} (${q.billed_to_role}).`;
  }
}

function SpendCaps({ caps }: { caps: SpendCap[] }) {
  if (caps.length === 0) return null;
  return (
    <dl className="caps">
      {caps.map((c) => {
        const left = Math.max(0, c.limit - c.used);
        const ratio = c.limit > 0 ? left / c.limit : 0;
        return (
          <div key={c.scope} className="cap-row">
            <dt>{c.label}</dt>
            <dd>
              <span className={`cap-bar cap-bar-${ratio < 0.15 ? 'low' : ratio < 0.4 ? 'mid' : 'ok'}`}>
                <span className="cap-fill" style={{ width: `${Math.round(ratio * 100)}%` }} />
              </span>
              <span className="cap-text">
                {left} of {c.limit} left
                {c.remaining_amount ? ` · ${amountLabel(c.remaining_amount)} headroom` : ''}
              </span>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function Compose({
  bot,
  caps,
  onQueueChanged,
}: {
  bot: Bot;
  caps: CapabilityResolver;
  onQueueChanged: () => void;
}) {
  const assets = useApi<Page<Asset>>(routes.content.assets(bot.id), { query: { limit: 20 } });

  // --- generation ---------------------------------------------------------
  const [kind, setKind] = useState<GenerationKind>('text');
  const [prompt, setPrompt] = useState('');
  const [count, setCount] = useState(1);
  const [quote, setQuote] = useState<GenerationQuote | null>(null);
  const [confirmGenerate, setConfirmGenerate] = useState(false);

  const quoteCall = useMutation<Record<string, unknown>, GenerationQuote>(
    routes.content.generationQuote(bot.id),
  );
  const generate = useMutation<Record<string, unknown>, GenerationJob>(
    routes.content.generations(bot.id),
    { idempotent: true },
  );

  const requestQuote = async () => {
    setQuote(null);
    const q = await quoteCall.run({ kind, mode: 'on_demand', prompt, count });
    if (q) setQuote(q);
  };

  // Any edit invalidates the quote: a stale quote is worse than none, because it
  // is a specific and wrong claim about what the actor is about to be charged.
  const invalidateQuote = () => {
    if (quote) setQuote(null);
  };

  // --- posting ------------------------------------------------------------
  const accounts = bot.social_accounts;
  const [accountId, setAccountId] = useState(preferredAccount(accounts)?.id ?? '');
  const [body, setBody] = useState('');
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);

  const send = useMutation<Record<string, unknown>, Action>(routes.content.actions(bot.id), {
    idempotent: true,
  });

  const submitAction = async () => {
    const created = await send.run({
      type: 'post',
      social_account_id: accountId,
      body,
      asset_ids: selectedAssets,
      target_ref: null,
      scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
    });
    if (created) {
      setBody('');
      setSelectedAssets([]);
      setScheduledAt('');
      send.reset();
      onQueueChanged();
    }
  };

  const generatedAssets = useMemo(
    () => (assets.data?.data ?? []).filter((a) => a.provenance.source === 'generated'),
    [assets.data],
  );

  return (
    <div className="compose">
      <Card title="Spend headroom">
        {bot.spend_caps.length > 0 ? (
          <SpendCaps caps={bot.spend_caps} />
        ) : (
          <p className="muted">No spend caps are set on this bot.</p>
        )}
      </Card>

      <Gate caps={caps} capability="generate">
        {(disabled) => (
          <Card title="Generate">
            <fieldset disabled={disabled}>
              <Field id="gen-kind" label="Kind">
                <select
                  id="gen-kind"
                  className="select"
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value as GenerationKind);
                    invalidateQuote();
                  }}
                >
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                </select>
              </Field>

              <Field id="gen-prompt" label="Prompt" hint="The character profile is applied on top of this.">
                <textarea
                  id="gen-prompt"
                  className="textarea"
                  rows={4}
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    invalidateQuote();
                  }}
                />
              </Field>

              <Field id="gen-count" label="How many">
                <input
                  id="gen-count"
                  className="input input-narrow"
                  type="number"
                  min={1}
                  max={20}
                  value={count}
                  onChange={(e) => {
                    setCount(Number(e.target.value));
                    invalidateQuote();
                  }}
                />
              </Field>

              <div className="row">
                <button
                  type="button"
                  className="btn"
                  onClick={() => void requestQuote()}
                  disabled={disabled || prompt.trim().length === 0 || quoteCall.pending}
                >
                  {quoteCall.pending ? 'Quoting…' : 'Quote this'}
                </button>

                {/* Step 2 of 2: generation only unlocks once a quote has been seen. */}
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={disabled || !quote || Boolean(quote.blocking_reason) || generate.pending}
                  onClick={() => setConfirmGenerate(true)}
                >
                  Generate
                </button>
              </div>

              {quoteCall.problem ? <InlineError problem={quoteCall.problem} /> : null}

              {quote ? (
                <div className="quote" aria-live="polite">
                  <h3>Before you generate</h3>
                  <p className="quote-headline">
                    {quote.count} × {quote.kind} · {amountLabel(quote.amount)}
                  </p>
                  <p>{fundingSentence(quote)}</p>
                  <p className="muted">Fee schedule v{quote.fee_schedule_version}.</p>
                  <SpendCaps caps={quote.spend_caps} />
                  {quote.blocking_reason ? (
                    <p className="field-error" role="alert">
                      {quote.blocking_reason}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="muted">Nothing is charged until you quote and then confirm.</p>
              )}

              {generate.problem ? <ErrorView problem={generate.problem} /> : null}

              {generate.data ? (
                <LiveRegion>
                  Generation {generate.data.status}. Generated content is saved to the library and is
                  not posted — send it as a separate step below.
                </LiveRegion>
              ) : null}
            </fieldset>
          </Card>
        )}
      </Gate>

      <Gate caps={caps} capability="post">
        {(disabled) => (
          <Card title="Post">
            <fieldset disabled={disabled}>
              <Field id="post-account" label="Account">
                <select
                  id="post-account"
                  className="select"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.platform} · @{a.external_handle}
                      {a.status === 'healthy' ? '' : ` (${humanize(a.status)})`}
                    </option>
                  ))}
                </select>
              </Field>

              <Field id="post-body" label="Body">
                <textarea
                  id="post-body"
                  className="textarea"
                  rows={5}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </Field>

              <fieldset className="asset-picker">
                <legend>Attach from the library</legend>
                {generatedAssets.length === 0 ? (
                  <p className="muted">Nothing generated yet.</p>
                ) : (
                  <ul className="asset-strip">
                    {generatedAssets.slice(0, 8).map((a) => {
                      const on = selectedAssets.includes(a.id);
                      return (
                        <li key={a.id}>
                          <label className="asset-chip">
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() =>
                                setSelectedAssets((prev) =>
                                  on ? prev.filter((x) => x !== a.id) : [...prev, a.id],
                                )
                              }
                            />
                            <span>{a.kind === 'text' ? (a.body ?? '').slice(0, 40) : a.mime_type}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </fieldset>

              <Field
                id="post-when"
                label="Send at"
                hint={`Leave empty to queue immediately. Times are read in ${bot.timezone}.`}
              >
                <input
                  id="post-when"
                  className="input"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
              </Field>

              <button
                type="button"
                className="btn btn-primary"
                disabled={disabled || !accountId || (body.trim() === '' && selectedAssets.length === 0)}
                onClick={() => setConfirmSend(true)}
              >
                Review and send
              </button>

              {send.problem ? (
                <ErrorView problem={send.problem}>
                  {send.problem.category ? (
                    <p className="banner-body">
                      This wording was refused by the content policy. Rewrite it and try again — a
                      block is not a queue item and will not retry.
                    </p>
                  ) : null}
                </ErrorView>
              ) : null}

              {send.data ? (
                <LiveRegion>
                  Queued as <Badge tone="info">{send.data.status}</Badge>.
                </LiveRegion>
              ) : null}
            </fieldset>
          </Card>
        )}
      </Gate>

      <Dialog
        open={confirmGenerate}
        onClose={() => setConfirmGenerate(false)}
        title="Generate this content?"
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmGenerate(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setConfirmGenerate(false);
                void generate.run({ kind, mode: 'on_demand', prompt, count });
              }}
            >
              Generate
            </button>
          </>
        }
      >
        {quote ? (
          <>
            <p>
              {quote.count} × {quote.kind}, {amountLabel(quote.amount)}.
            </p>
            <p>{fundingSentence(quote)}</p>
            <p className="muted">
              This generates only. Nothing is posted until you send it as a separate step.
            </p>
          </>
        ) : null}
      </Dialog>

      <Dialog
        open={confirmSend}
        onClose={() => setConfirmSend(false)}
        title="Send this?"
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmSend(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setConfirmSend(false);
                void submitAction();
              }}
            >
              {scheduledAt ? 'Schedule it' : 'Send it'}
            </button>
          </>
        }
      >
        <p>
          Posting to{' '}
          <strong>
            {accounts.find((a) => a.id === accountId)?.platform} @
            {accounts.find((a) => a.id === accountId)?.external_handle}
          </strong>
          .
        </p>
        <blockquote className="preview">{body || '(assets only)'}</blockquote>
        {selectedAssets.length > 0 ? <p>{selectedAssets.length} attachment(s).</p> : null}
      </Dialog>
    </div>
  );
}
